import {
  Button,
  CloseIcon,
  TextInput,
  Dialog,
  DialogButtons,
  DialogTip,
  Spinner,
  QuestionnaireProgressBar
} from '@st/theme'
import { match } from '@st/pattern'
import {
  selIdentityVerificationDialog,
  type Identity,
  type IdentityVerificationAction,
  type IdentityVerificationMessage,
  type IdentityVerificationState
} from './identity-verification-module'

/** Renders the verification dialog model and sends intent. Closing belongs to the parent. */
export function IdentityVerificationPanel({
  state,
  isArchived,
  signaturesSaved,
  onMessage,
  onClose
}: {
  state: IdentityVerificationState
  isArchived: boolean
  signaturesSaved: boolean
  onMessage: (message: IdentityVerificationMessage) => void
  onClose: () => void
}) {
  const dialog = selIdentityVerificationDialog(state, isArchived, signaturesSaved)
  if (!dialog) return null

  function act(action: IdentityVerificationAction) {
    const message = action.message
    if (message.type == 'close') onClose()
    else onMessage(message)
  }

  function changeIdentity(field: keyof Identity, value: string) {
    return onMessage({ type: 'identityChanged', field, sensitiveValue: value })
  }

  return (
    <div className="absolute inset-0 z-30 flex items-center justify-center bg-stone-500/60 p-3">
      <Dialog
        title={
          <div className="flex items-start justify-between gap-4">
            <span>{dialog.title}</span>
            <button
              aria-label="Close verification"
              disabled={!dialog.closeEnabled}
              onClick={onClose}
            >
              <CloseIcon className="size-4" />
            </button>
          </div>
        }
        subtitle={dialog.subtitle && <span className="whitespace-pre-line">{dialog.subtitle}</span>}
        tip={dialog.tip && <DialogTip>{dialog.tip}</DialogTip>}
        buttons={
          <DialogButtons>
            {dialog.actions.map((action) => (
              <Button
                key={action.message.type}
                variant={action.variant}
                size="l"
                leadingIcon={action.busy && <Spinner className="size-4" />}
                disabled={!action.enabled}
                onClick={() => act(action)}
              >
                {action.label}
              </Button>
            ))}
          </DialogButtons>
        }
      >
        {match(dialog.body)
          .with({ type: 'identity' }, ({ identity, editable }) => (
            <div className="grid gap-3 sm:grid-cols-2">
              <IdentityInput
                label="First name"
                value={identity.firstName}
                disabled={!editable}
                onChange={(value) => changeIdentity('firstName', value)}
              />
              <IdentityInput
                label="Last name"
                value={identity.lastName}
                disabled={!editable}
                onChange={(value) => changeIdentity('lastName', value)}
              />
              <IdentityInput
                label="Date of birth"
                value={identity.dateOfBirth}
                disabled={!editable}
                type="date"
                onChange={(value) => changeIdentity('dateOfBirth', value)}
              />
              <IdentityInput
                label="Social Security number"
                value={identity.ssn}
                disabled={!editable}
                autoComplete="off"
                type="password"
                onChange={(value) => changeIdentity('ssn', value)}
              />
            </div>
          ))
          .with({ type: 'question' }, (body) => (
            <>
              <div className="mb-5">
                <div className="mb-2 text-xs text-stone-500">{body.progressLabel}</div>
                <div className="h-1">
                  <QuestionnaireProgressBar value={body.progress} />
                </div>
              </div>
              <h2 className="mb-4 text-lg font-medium">{body.question.prompt}</h2>
              <fieldset className="flex flex-col gap-2">
                <legend className="sr-only">{body.question.prompt}</legend>
                {body.question.options.map((option) => (
                  <AnswerOption
                    key={option.id}
                    name={`identity-question-${body.question.id}`}
                    label={option.label}
                    selected={body.selectedAnswerId == option.id}
                    disabled={!body.answerable}
                    onSelect={() =>
                      onMessage({
                        type: 'answerSelected',
                        questionId: body.question.id,
                        sensitiveAnswerId: option.id
                      })
                    }
                  />
                ))}
              </fieldset>
            </>
          ))
          .with({ type: 'notice' }, () => null)
          .exhaustive()}
      </Dialog>
    </div>
  )
}

/**
 * A real radio inside the card, not a button playing one: the options of a
 * question share a `name`, so the browser gives the group arrow-key
 * navigation, roving tab order, and form semantics for free. The card is the
 * label, so the whole row is the click target, and focus is drawn on the card
 * via `focus-within` because the input itself is what receives focus.
 */
function AnswerOption({
  name,
  label,
  selected,
  disabled,
  onSelect
}: {
  name: string
  label: string
  selected: boolean
  disabled: boolean
  onSelect: () => void
}) {
  let appearance = 'border-gray-300 bg-white text-gray-900 hover:border-blue-500 hover:bg-blue-50'
  if (selected) appearance = 'border-blue-500 bg-blue-100 text-blue-800'
  let interactivity = 'cursor-pointer'
  if (disabled) interactivity = 'cursor-default opacity-80'

  return (
    <label
      className={`flex items-center gap-3 rounded-md border px-4 py-3 text-left text-sm focus-within:outline focus-within:outline-2 focus-within:outline-offset-2 focus-within:outline-blue-600 ${appearance} ${interactivity}`}
    >
      <input
        type="radio"
        className="size-4 shrink-0"
        name={name}
        checked={selected}
        disabled={disabled}
        onChange={onSelect}
      />
      <span>{label}</span>
    </label>
  )
}

function IdentityInput({
  label,
  value,
  disabled,
  autoComplete,
  type,
  onChange
}: {
  label: string
  value: string
  disabled: boolean
  autoComplete?: string
  type?: 'text' | 'password' | 'date'
  onChange: (value: string) => void
}) {
  return (
    <label className="flex flex-col gap-1 text-sm font-medium text-gray-700">
      {label}
      <TextInput
        className="w-full"
        value={value}
        disabled={disabled}
        autoComplete={autoComplete}
        type={type}
        onChange={onChange}
      />
    </label>
  )
}
